import React, { useMemo, useState } from 'react';
import { buildAxisTicks, getBarChartAxisMax } from '../../utils/profilesSourcedAggregates';

const BAR_COLORS = ['#2563EB', '#16A34A', '#F59E0B', '#8B5CF6'];
const DEFAULT_VISIBLE = 8;

function ProfilesSourcedBarChartCard({
  title,
  subtitle = '',
  items = [],
  axisLabel = 'Profiles Sourced',
  maxVisible = DEFAULT_VISIBLE,
  sortBy = 'count',
  emptyMessage = 'No data for the selected filters.',
}) {
  const [expanded, setExpanded] = useState(false);

  const sortedItems = useMemo(() => {
    const copy = [...items];
    if (sortBy === 'chronological') {
      return copy.sort(
        (a, b) => String(a.sortKey || a.label).localeCompare(String(b.sortKey || b.label)),
      );
    }
    return copy.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [items, sortBy]);

  const visibleItems = expanded ? sortedItems : sortedItems.slice(0, maxVisible);
  const maxCount = Math.max(...sortedItems.map((item) => item.count), 0);
  const axisMax = getBarChartAxisMax(maxCount);
  const axisTicks = buildAxisTicks(axisMax);

  return (
    <article className="hr-bar-chart-card">
      <div className="hr-bar-chart-header">
        <h4 className="hr-bar-chart-title">{title}</h4>
        {subtitle ? <p className="hr-bar-chart-subtitle mb-0">{subtitle}</p> : null}
      </div>

      {sortedItems.length === 0 ? (
        <p className="hr-bar-chart-empty">{emptyMessage}</p>
      ) : (
        <>
          <div className={`hr-bar-chart-body${sortedItems.length > maxVisible ? ' is-scrollable' : ''}`}>
            {visibleItems.map((item, index) => {
              const widthPercent = axisMax > 0
                ? Math.max((item.count / axisMax) * 100, item.count > 0 ? 6 : 0)
                : 0;
              const color = BAR_COLORS[index % BAR_COLORS.length];

              return (
                <div key={`${item.label}-${index}`} className="hr-bar-chart-row">
                  <span className="hr-bar-chart-label" title={item.label}>{item.label}</span>
                  <div className="hr-bar-chart-track-wrap">
                    <div className="hr-bar-chart-track">
                      <div
                        className="hr-bar-chart-fill"
                        style={{ width: `${widthPercent}%`, backgroundColor: color }}
                      />
                    </div>
                    <span className="hr-bar-chart-count">{item.count}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="hr-bar-chart-axis-wrap">
            <div className="hr-bar-chart-axis" aria-hidden="true">
              <div className="hr-bar-chart-axis-spacer" />
              <div className="hr-bar-chart-axis-line">
                {axisTicks.map((tick) => (
                  <span key={tick}>{tick}</span>
                ))}
              </div>
            </div>
            <p className="hr-bar-chart-axis-label">{axisLabel}</p>
          </div>

          {sortedItems.length > maxVisible && (
            <button
              type="button"
              className="hr-bar-chart-more-btn"
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? 'Show Less' : `View More (${sortedItems.length - maxVisible})`}
            </button>
          )}
        </>
      )}
    </article>
  );
}

export default ProfilesSourcedBarChartCard;
